import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Settings2, Plus, Search, Layers, GitCompare, Clock, Trash2, Eye, CheckCircle2, XCircle, AlertTriangle, Loader2, Archive } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";
import { DropdownBadge, DropdownSelect } from "../components/shared/DropdownHooks";

export default function ConfigMgmtPage({ onLogout, userRole, isFeatureEnabled }) {
  const [tab, setTab] = useState("items");
  const [items, setItems] = useState([]);
  const [baselines, setBaselines] = useState([]);
  const [alterations, setAlterations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [baselineOpen, setBaselineOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [altOpen, setAltOpen] = useState(false);

  const [ciForm, setCiForm] = useState({ name: "", ci_number: "", ci_type: "machine", location: "", linked_machine: "", notes: "",
    ad_spec: "", ad_revision: "", ad_material: "", ad_drawing: "",
    ab_serial: "", ab_install_date: "", ab_actual_material: "", ab_deviation: "",
    ao_condition: "", ao_last_inspection: "", ao_modifications: "", ao_performance: "" });

  const [blForm, setBlForm] = useState({ name: "", baseline_type: "design", description: "" });
  const [altForm, setAltForm] = useState({ ci_id: "", title: "", description: "", justification: "", expires_at: "", changes_field: "", changes_from: "", changes_to: "" });
  const [compareForm, setCompareForm] = useState({ baseline_a_id: "", baseline_b_id: "", config_key: "as_designed" });

  const isAdmin = userRole === "admin";

  const fetchAll = useCallback(async () => {
    try {
      const [itemsR, blR, altR, statsR] = await Promise.allSettled([
        axios.get(`${API}/config-items`, { headers: getAuthHeaders() }),
        axios.get(`${API}/config-baselines`, { headers: getAuthHeaders() }),
        axios.get(`${API}/config-alterations`, { headers: getAuthHeaders() }),
        axios.get(`${API}/config-mgmt/stats`, { headers: getAuthHeaders() }),
      ]);
      if (itemsR.status === "fulfilled") setItems(itemsR.value.data || []);
      if (blR.status === "fulfilled") setBaselines(blR.value.data || []);
      if (altR.status === "fulfilled") setAlterations(altR.value.data || []);
      if (statsR.status === "fulfilled") setStats(statsR.value.data || {});
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleCreateCI = async () => {
    if (!ciForm.name.trim()) { toast.error("Name required"); return; }
    try {
      const payload = {
        name: ciForm.name, ci_number: ciForm.ci_number, ci_type: ciForm.ci_type,
        location: ciForm.location, linked_machine: ciForm.linked_machine, notes: ciForm.notes,
        as_designed: { spec: ciForm.ad_spec, revision: ciForm.ad_revision, material: ciForm.ad_material, drawing: ciForm.ad_drawing },
        as_built: { serial: ciForm.ab_serial, install_date: ciForm.ab_install_date, actual_material: ciForm.ab_actual_material, deviation: ciForm.ab_deviation },
        as_operated: { condition: ciForm.ao_condition, last_inspection: ciForm.ao_last_inspection, modifications: ciForm.ao_modifications, performance: ciForm.ao_performance },
      };
      await axios.post(`${API}/config-items`, payload, { headers: getAuthHeaders() });
      toast.success("Configuration item created");
      setCreateOpen(false);
      setCiForm({ name: "", ci_number: "", ci_type: "machine", location: "", linked_machine: "", notes: "", ad_spec: "", ad_revision: "", ad_material: "", ad_drawing: "", ab_serial: "", ab_install_date: "", ab_actual_material: "", ab_deviation: "", ao_condition: "", ao_last_inspection: "", ao_modifications: "", ao_performance: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCreateBaseline = async () => {
    if (!blForm.name.trim()) { toast.error("Baseline name required"); return; }
    try {
      await axios.post(`${API}/config-baselines`, { ...blForm, ci_ids: [] }, { headers: getAuthHeaders() });
      toast.success("Baseline snapshot created");
      setBaselineOpen(false);
      setBlForm({ name: "", baseline_type: "design", description: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const handleCompare = async () => {
    if (!compareForm.baseline_a_id) { toast.error("Select baseline A"); return; }
    try {
      const resp = await axios.post(`${API}/config-baselines/compare`, compareForm, { headers: getAuthHeaders() });
      setCompareResult(resp.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Comparison failed"); }
  };

  const handleCreateAlt = async () => {
    if (!altForm.ci_id || !altForm.title.trim()) { toast.error("CI and title required"); return; }
    try {
      const payload = {
        ...altForm,
        changes: { field: altForm.changes_field, from_value: altForm.changes_from, to_value: altForm.changes_to },
      };
      await axios.post(`${API}/config-alterations`, payload, { headers: getAuthHeaders() });
      toast.success("Temporary alteration created");
      setAltOpen(false);
      setAltForm({ ci_id: "", title: "", description: "", justification: "", expires_at: "", changes_field: "", changes_from: "", changes_to: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const approveAlt = async (id) => {
    try {
      await axios.post(`${API}/config-alterations/${id}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("Alteration approved"); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const closeAlt = async (id) => {
    try {
      await axios.post(`${API}/config-alterations/${id}/close`, {}, { headers: getAuthHeaders() });
      toast.success("Alteration closed"); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteItem = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await axios.delete(`${API}/${type}/${id}`, { headers: getAuthHeaders() });
      toast.success("Deleted"); fetchAll();
    } catch { toast.error("Failed"); }
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64" data-testid="config-loading"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  const filteredItems = items.filter(ci => {
    if (typeFilter && ci.ci_type !== typeFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return ci.name?.toLowerCase().includes(q) || ci.ci_number?.toLowerCase().includes(q) || ci.linked_machine?.toLowerCase().includes(q);
  });

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="config-mgmt-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Configuration Management" subtitle="As-Designed / As-Built / As-Operated tracking" testId="config-mgmt-title" />
          <div className="flex gap-2">
            <Button onClick={() => setCreateOpen(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold" data-testid="create-ci-btn"><Plus size={14} className="mr-1" /> New CI</Button>
            <Button onClick={() => setBaselineOpen(true)} variant="outline" className="rounded-none text-xs font-bold border-[#7C3AED] text-[#7C3AED]" data-testid="create-baseline-btn"><Layers size={14} className="mr-1" /> Snapshot</Button>
            <Button onClick={() => { setCompareOpen(true); setCompareResult(null); }} variant="outline" className="rounded-none text-xs font-bold border-[#D97706] text-[#D97706]" data-testid="compare-btn"><GitCompare size={14} className="mr-1" /> Compare</Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="config-stats">
          <StatCard label="Total CIs" value={stats.total_cis || 0} color="#0F172A" />
          <StatCard label="Active CIs" value={stats.active_cis || 0} color="#10B981" />
          <StatCard label="Baselines" value={stats.baselines || 0} color="#7C3AED" />
          <StatCard label="Active Alts" value={stats.active_alterations || 0} color="#2563EB" />
          <StatCard label="Pending Alts" value={stats.pending_alterations || 0} color="#F59E0B" />
          <StatCard label="Expired Alts" value={stats.expired_alterations || 0} color="#DC2626" />
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex">
            {[
              { key: "items", label: `CIs (${items.length})` },
              { key: "baselines", label: `Baselines (${baselines.length})` },
              { key: "alterations", label: `Alterations (${alterations.length})` },
            ].map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} className={`px-5 py-2.5 text-xs font-bold tracking-wider border transition-colors ${tab === t.key ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#64748B] border-[#E2E8F0] hover:bg-[#F8FAFC]"}`} data-testid={`tab-${t.key}`}>{t.label}</button>
            ))}
          </div>
          {tab === "items" && (
            <>
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search CIs..." className="w-full pl-9 pr-3 py-2.5 border border-[#E2E8F0] text-xs bg-white focus:border-[#2563EB] focus:outline-none" data-testid="config-search" />
              </div>
              <DropdownSelect dropdownKey="ci_types" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border border-[#E2E8F0] px-3 py-2.5 text-xs bg-white min-w-[130px]" testId="ci-type-filter" placeholder="All Types" />
            </>
          )}
        </div>

        {/* CI List */}
        {tab === "items" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="ci-list">
            {filteredItems.length === 0 ? (
              <div className="p-12 text-center"><Settings2 size={36} className="mx-auto mb-2 text-[#CBD5E1]" /><p className="text-sm text-[#94A3B8]">No configuration items yet.</p></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#FAFAFA] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">CI #</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">Name</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">Type</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">Status</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">Machine</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">As-Designed</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">As-Built</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-left">As-Operated</th>
                      <th className="px-4 py-3 text-xs font-bold text-[#0F172A] tracking-wider uppercase text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F1F5F9]">
                    {filteredItems.map(ci => (
                      <tr key={ci.id} className="hover:bg-[#FAFAFA] transition-colors" data-testid={`ci-${ci.id}`}>
                        <td className="px-4 py-3 font-mono text-xs text-[#2563EB] font-bold">{ci.ci_number}</td>
                        <td className="px-4 py-3 text-sm text-[#0F172A]">{ci.name}</td>
                        <td className="px-4 py-3"><DropdownBadge dropdownKey="ci_types" value={ci.ci_type} /></td>
                        <td className="px-4 py-3"><DropdownBadge dropdownKey="ci_statuses" value={ci.status} /></td>
                        <td className="px-4 py-3 text-xs font-mono text-[#64748B]">{ci.linked_machine || "—"}</td>
                        <td className="px-4 py-3"><ConfigSummary data={ci.as_designed} /></td>
                        <td className="px-4 py-3"><ConfigSummary data={ci.as_built} /></td>
                        <td className="px-4 py-3"><ConfigSummary data={ci.as_operated} /></td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="outline" onClick={() => setDetailItem(ci)} className="rounded-none text-[10px]" data-testid={`view-ci-${ci.id}`}><Eye size={11} /></Button>
                            <Button size="sm" variant="outline" onClick={() => { setAltOpen(true); setAltForm(f => ({ ...f, ci_id: ci.id })); }} className="rounded-none border-[#D97706] text-[#D97706] text-[10px]" title="New temp alteration" data-testid={`alt-ci-${ci.id}`}><Clock size={11} /></Button>
                            {isAdmin && <Button size="sm" variant="outline" onClick={() => deleteItem("config-items", ci.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]" data-testid={`delete-ci-${ci.id}`}><Trash2 size={11} /></Button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Baselines List */}
        {tab === "baselines" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="baseline-list">
            {baselines.length === 0 ? (
              <div className="p-12 text-center"><Layers size={36} className="mx-auto mb-2 text-[#CBD5E1]" /><p className="text-sm text-[#94A3B8]">No baselines yet. Create a snapshot.</p></div>
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {baselines.map(bl => (
                  <div key={bl.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`baseline-${bl.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-[#0F172A]">{bl.name}</span>
                        <DropdownBadge dropdownKey="baseline_types" value={bl.baseline_type} />
                      </div>
                      <p className="text-[11px] text-[#64748B] mt-0.5">{bl.item_count} items · Created by {bl.created_by_name} · {new Date(bl.frozen_at).toLocaleString()}</p>
                      {bl.description && <p className="text-xs text-[#475569] mt-0.5">{bl.description}</p>}
                    </div>
                    <div className="flex gap-1">
                      {isAdmin && <Button size="sm" variant="outline" onClick={() => deleteItem("config-baselines", bl.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]"><Trash2 size={11} /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Alterations List */}
        {tab === "alterations" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="alteration-list">
            {alterations.length === 0 ? (
              <div className="p-12 text-center"><Clock size={36} className="mx-auto mb-2 text-[#CBD5E1]" /><p className="text-sm text-[#94A3B8]">No temporary alterations.</p></div>
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {alterations.map(alt => (
                  <div key={alt.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`alt-${alt.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs text-[#D97706] font-bold">{alt.alt_number}</span>
                        <DropdownBadge dropdownKey="alteration_statuses" value={alt.status} />
                        <span className="text-[10px] text-[#94A3B8]">on {alt.ci_number} ({alt.ci_name})</span>
                      </div>
                      <p className="text-sm font-medium text-[#0F172A] mt-1">{alt.title}</p>
                      <p className="text-[11px] text-[#64748B]">
                        By {alt.created_by_name} · {new Date(alt.created_at).toLocaleDateString()}
                        {alt.expires_at && <span className={`ml-2 font-bold ${alt.status === "expired" ? "text-[#DC2626]" : "text-[#D97706]"}`}>Expires: {alt.expires_at.split("T")[0]}</span>}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {alt.status === "pending" && isAdmin && <Button size="sm" onClick={() => approveAlt(alt.id)} className="rounded-none bg-[#10B981] text-white text-[10px] font-bold" data-testid={`approve-alt-${alt.id}`}><CheckCircle2 size={11} className="mr-1" />Approve</Button>}
                      {(alt.status === "active" || alt.status === "expired") && <Button size="sm" onClick={() => closeAlt(alt.id)} variant="outline" className="rounded-none text-[10px] font-bold" data-testid={`close-alt-${alt.id}`}><XCircle size={11} className="mr-1" />Close</Button>}
                      {isAdmin && <Button size="sm" variant="outline" onClick={() => deleteItem("config-alterations", alt.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]"><Trash2 size={11} /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create CI Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="create-ci-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">New Configuration Item</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Track an item across as-designed, as-built, and as-operated states.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Name *</Label><Input value={ciForm.name} onChange={e => setCiForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="ci-name" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">CI Number</Label><Input value={ciForm.ci_number} onChange={e => setCiForm(f => ({ ...f, ci_number: e.target.value }))} placeholder="Auto-generated" className="mt-1 rounded-none text-xs font-mono" data-testid="ci-number" /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Type</Label><DropdownSelect dropdownKey="ci_types" value={ciForm.ci_type} onChange={e => setCiForm(f => ({ ...f, ci_type: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" testId="ci-type" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Location</Label><Input value={ciForm.location} onChange={e => setCiForm(f => ({ ...f, location: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="ci-location" /></div>
              <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Linked Machine</Label><Input value={ciForm.linked_machine} onChange={e => setCiForm(f => ({ ...f, linked_machine: e.target.value }))} placeholder="SM-001" className="mt-1 rounded-none text-xs font-mono" data-testid="ci-machine" /></div>
            </div>

            {/* As-Designed */}
            <fieldset className="border border-[#BFDBFE] p-3">
              <legend className="text-[10px] font-bold uppercase tracking-wider text-[#2563EB] px-1">As-Designed</legend>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px] text-[#64748B]">Spec / Drawing #</Label><Input value={ciForm.ad_spec} onChange={e => setCiForm(f => ({ ...f, ad_spec: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ad-spec" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Revision</Label><Input value={ciForm.ad_revision} onChange={e => setCiForm(f => ({ ...f, ad_revision: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ad-revision" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Material</Label><Input value={ciForm.ad_material} onChange={e => setCiForm(f => ({ ...f, ad_material: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ad-material" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Drawing Ref</Label><Input value={ciForm.ad_drawing} onChange={e => setCiForm(f => ({ ...f, ad_drawing: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ad-drawing" /></div>
              </div>
            </fieldset>

            {/* As-Built */}
            <fieldset className="border border-[#DDD6FE] p-3">
              <legend className="text-[10px] font-bold uppercase tracking-wider text-[#7C3AED] px-1">As-Built</legend>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px] text-[#64748B]">Serial #</Label><Input value={ciForm.ab_serial} onChange={e => setCiForm(f => ({ ...f, ab_serial: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ab-serial" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Install Date</Label><Input type="date" value={ciForm.ab_install_date} onChange={e => setCiForm(f => ({ ...f, ab_install_date: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ab-install" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Actual Material</Label><Input value={ciForm.ab_actual_material} onChange={e => setCiForm(f => ({ ...f, ab_actual_material: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ab-material" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Build Deviation</Label><Input value={ciForm.ab_deviation} onChange={e => setCiForm(f => ({ ...f, ab_deviation: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ab-deviation" /></div>
              </div>
            </fieldset>

            {/* As-Operated */}
            <fieldset className="border border-[#BBF7D0] p-3">
              <legend className="text-[10px] font-bold uppercase tracking-wider text-[#10B981] px-1">As-Operated</legend>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-[10px] text-[#64748B]">Condition</Label><Input value={ciForm.ao_condition} onChange={e => setCiForm(f => ({ ...f, ao_condition: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ao-condition" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Last Inspection</Label><Input type="date" value={ciForm.ao_last_inspection} onChange={e => setCiForm(f => ({ ...f, ao_last_inspection: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ao-inspection" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Modifications</Label><Input value={ciForm.ao_modifications} onChange={e => setCiForm(f => ({ ...f, ao_modifications: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ao-modifications" /></div>
                <div><Label className="text-[10px] text-[#64748B]">Performance</Label><Input value={ciForm.ao_performance} onChange={e => setCiForm(f => ({ ...f, ao_performance: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="ci-ao-performance" /></div>
              </div>
            </fieldset>

            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Notes</Label><textarea value={ciForm.notes} onChange={e => setCiForm(f => ({ ...f, notes: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-14 resize-none" data-testid="ci-notes" /></div>
            <Button onClick={handleCreateCI} className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-none text-xs font-bold" data-testid="ci-submit-btn">Create Configuration Item</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* CI Detail Dialog */}
      <Dialog open={!!detailItem} onOpenChange={(o) => { if (!o) setDetailItem(null); }}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="ci-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">{detailItem?.ci_number} — {detailItem?.name}</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Configuration item detail with all three states.</DialogDescription>
          </DialogHeader>
          {detailItem && (
            <div className="space-y-4 mt-2">
              <div className="flex gap-2 flex-wrap">
                <DropdownBadge dropdownKey="ci_types" value={detailItem.ci_type} />
                <DropdownBadge dropdownKey="ci_statuses" value={detailItem.status} />
                {detailItem.linked_machine && <span className="text-[10px] font-mono px-2 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]">{detailItem.linked_machine}</span>}
              </div>
              <ConfigStatePanel label="As-Designed" color="#2563EB" data={detailItem.as_designed} />
              <ConfigStatePanel label="As-Built" color="#7C3AED" data={detailItem.as_built} />
              <ConfigStatePanel label="As-Operated" color="#10B981" data={detailItem.as_operated} />
              {detailItem.notes && <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Notes</Label><p className="text-sm text-[#475569] mt-1">{detailItem.notes}</p></div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Baseline Dialog */}
      <Dialog open={baselineOpen} onOpenChange={setBaselineOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="baseline-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Create Baseline Snapshot</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Freeze current state of all active CIs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Name *</Label><Input value={blForm.name} onChange={e => setBlForm(f => ({ ...f, name: e.target.value }))} className="mt-1 rounded-none text-sm" placeholder="Q1 2026 Design Baseline" data-testid="bl-name" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Type</Label><DropdownSelect dropdownKey="baseline_types" value={blForm.baseline_type} onChange={e => setBlForm(f => ({ ...f, baseline_type: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm" testId="bl-type" /></div>
            <div><Label className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">Description</Label><textarea value={blForm.description} onChange={e => setBlForm(f => ({ ...f, description: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-14 resize-none" data-testid="bl-description" /></div>
            <Button onClick={handleCreateBaseline} className="w-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white rounded-none text-xs font-bold" data-testid="bl-submit-btn">Create Snapshot</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Compare Dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="compare-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Baseline Comparison / Discrepancy Detection</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Compare two baselines or a baseline vs current live state.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] font-bold uppercase text-[#64748B]">Baseline A *</Label>
                <select value={compareForm.baseline_a_id} onChange={e => setCompareForm(f => ({ ...f, baseline_a_id: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-xs" data-testid="compare-a">
                  <option value="">Select...</option>
                  {baselines.map(bl => <option key={bl.id} value={bl.id}>{bl.name} ({bl.baseline_type})</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[10px] font-bold uppercase text-[#64748B]">Baseline B (empty = live)</Label>
                <select value={compareForm.baseline_b_id || ""} onChange={e => setCompareForm(f => ({ ...f, baseline_b_id: e.target.value || null }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-xs" data-testid="compare-b">
                  <option value="">Current Live State</option>
                  {baselines.map(bl => <option key={bl.id} value={bl.id}>{bl.name} ({bl.baseline_type})</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#64748B]">Compare Field</Label>
              <select value={compareForm.config_key} onChange={e => setCompareForm(f => ({ ...f, config_key: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-xs" data-testid="compare-key">
                <option value="as_designed">As-Designed</option>
                <option value="as_built">As-Built</option>
                <option value="as_operated">As-Operated</option>
              </select>
            </div>
            <Button onClick={handleCompare} className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none text-xs font-bold" data-testid="compare-submit-btn">Run Comparison</Button>

            {/* Results */}
            {compareResult && (
              <div className="border-t border-[#E2E8F0] pt-3 space-y-2" data-testid="compare-results">
                <div className="flex items-center gap-3 text-xs">
                  <span className="font-bold text-[#0F172A]">{compareResult.baseline_a.name}</span>
                  <GitCompare size={14} className="text-[#94A3B8]" />
                  <span className="font-bold text-[#0F172A]">{compareResult.baseline_b.name}</span>
                  <span className="ml-auto text-[#64748B]">{compareResult.total_items} items compared</span>
                </div>
                {compareResult.discrepancy_count === 0 ? (
                  <div className="p-4 bg-[#F0FDF4] border border-[#BBF7D0] text-center text-sm text-[#10B981] font-bold">No discrepancies found — baselines match.</div>
                ) : (
                  <div className="space-y-2">
                    <div className="text-xs font-bold text-[#DC2626]"><AlertTriangle size={12} className="inline mr-1" />{compareResult.discrepancy_count} discrepancy(ies) found</div>
                    {compareResult.discrepancies.map((d, i) => (
                      <div key={i} className="p-2.5 border border-[#E2E8F0] bg-[#FAFAFA] text-xs" data-testid={`discrepancy-${i}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-[#2563EB]">{d.ci_number}</span>
                          <span className="text-[#0F172A] font-medium">{d.name}</span>
                          <span className={`ml-auto px-2 py-0.5 text-[10px] font-bold uppercase ${d.type === "added" ? "text-[#10B981] bg-[#F0FDF4] border border-[#BBF7D0]" : d.type === "removed" ? "text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA]" : "text-[#D97706] bg-[#FFFBEB] border border-[#FDE68A]"}`}>{d.type}</span>
                        </div>
                        {d.details && <p className="text-[#64748B] mt-1">{d.details}</p>}
                        {d.field_diffs && (
                          <div className="mt-1 space-y-0.5">
                            {d.field_diffs.map((fd, j) => (
                              <div key={j} className="flex gap-2 text-[11px]">
                                <span className="font-mono text-[#64748B] w-24">{fd.field}:</span>
                                <span className="text-[#DC2626] line-through">{fd.baseline_a ?? "—"}</span>
                                <span className="text-[#94A3B8]">→</span>
                                <span className="text-[#10B981]">{fd.baseline_b ?? "—"}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Alteration Dialog */}
      <Dialog open={altOpen} onOpenChange={setAltOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-md" data-testid="alteration-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">New Temporary Alteration</DialogTitle>
            <DialogDescription className="text-xs text-[#64748B]">Request a temporary deviation from baseline configuration.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-[10px] font-bold uppercase text-[#64748B]">Configuration Item *</Label>
              <select value={altForm.ci_id} onChange={e => setAltForm(f => ({ ...f, ci_id: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-xs" data-testid="alt-ci">
                <option value="">Select CI...</option>
                {items.map(ci => <option key={ci.id} value={ci.id}>{ci.ci_number} — {ci.name}</option>)}
              </select>
            </div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Title *</Label><Input value={altForm.title} onChange={e => setAltForm(f => ({ ...f, title: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="alt-title" /></div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Description</Label><textarea value={altForm.description} onChange={e => setAltForm(f => ({ ...f, description: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-14 resize-none" data-testid="alt-description" /></div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Justification</Label><textarea value={altForm.justification} onChange={e => setAltForm(f => ({ ...f, justification: e.target.value }))} className="mt-1 w-full border border-[#E2E8F0] p-2 text-sm h-14 resize-none" data-testid="alt-justification" /></div>
            <div><Label className="text-[10px] font-bold uppercase text-[#64748B]">Expiration Date</Label><Input type="date" value={altForm.expires_at} onChange={e => setAltForm(f => ({ ...f, expires_at: e.target.value }))} className="mt-1 rounded-none text-sm" data-testid="alt-expires" /></div>
            <fieldset className="border border-[#FDE68A] p-3">
              <legend className="text-[10px] font-bold uppercase text-[#D97706] px-1">Change Details</legend>
              <div className="space-y-2">
                <div><Label className="text-[10px] text-[#64748B]">Field Being Changed</Label><Input value={altForm.changes_field} onChange={e => setAltForm(f => ({ ...f, changes_field: e.target.value }))} placeholder="e.g., material, spec" className="mt-0.5 rounded-none text-xs" data-testid="alt-field" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px] text-[#64748B]">From (Baseline)</Label><Input value={altForm.changes_from} onChange={e => setAltForm(f => ({ ...f, changes_from: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="alt-from" /></div>
                  <div><Label className="text-[10px] text-[#64748B]">To (Temporary)</Label><Input value={altForm.changes_to} onChange={e => setAltForm(f => ({ ...f, changes_to: e.target.value }))} className="mt-0.5 rounded-none text-xs" data-testid="alt-to" /></div>
                </div>
              </div>
            </fieldset>
            <Button onClick={handleCreateAlt} className="w-full bg-[#D97706] hover:bg-[#B45309] text-white rounded-none text-xs font-bold" data-testid="alt-submit-btn">Submit Alteration Request</Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

/* ─── Sub-Components ─── */

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white border border-[#E2E8F0] px-4 py-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
      <p className="text-2xl font-heading font-black mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

function ConfigSummary({ data }) {
  if (!data || Object.keys(data).length === 0) return <span className="text-[10px] text-[#CBD5E1]">—</span>;
  const filled = Object.entries(data).filter(([, v]) => v).length;
  const total = Object.keys(data).length;
  return <span className="text-[10px] font-mono text-[#64748B]">{filled}/{total} fields</span>;
}

function ConfigStatePanel({ label, color, data }) {
  const entries = Object.entries(data || {}).filter(([, v]) => v);
  return (
    <div className="border p-3" style={{ borderColor: `${color}40` }}>
      <h4 className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color }}>{label}</h4>
      {entries.length === 0 ? (
        <p className="text-[11px] text-[#94A3B8]">No data recorded</p>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {entries.map(([k, v]) => (
            <div key={k} className="text-xs">
              <span className="text-[#94A3B8] capitalize">{k.replace(/_/g, " ")}: </span>
              <span className="text-[#0F172A] font-medium">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
